import * as THREE from "three";
import { createLightningExplosion } from "../effects/LightningExplosion";
import { handleProjectileHit } from "../damage";
import { Tank } from "../Tank";

// Sound effect for thunder - to be implemented if audio system exists
// import { playSound } from "../audio/SoundSystem"; // Uncomment if sound system exists

const PROJECTILE_CONFIG = {
  SPEED: 3.0, // Faster than regular projectile
  RANGE: 15.0, // Longer range
  LIGHTNING_COLOR: 0x00ffff, // Cyan blue color for lightning
  LIGHTNING_INTENSITY: 3, // Brighter light
  LIGHTNING_RANGE: 15, // Longer light range
  LIGHTNING_SEGMENTS: 5, // Number of lightning segments
  LIGHTNING_WIDTH: 0.1, // Width of lightning beam
  LIGHTNING_BRANCH_PROBABILITY: 0.3, // Probability of creating a branch
  LIGHTNING_BRANCH_ANGLE: Math.PI / 6, // Maximum angle of a branch
};

/**
 * Tworzy i animuje pocisk błyskawicy (Odyna) wystrzelony z podanej pozycji w podanym kierunku
 * @param scene Scena Three.js
 * @param startPosition Pozycja początkowa pocisku
 * @param direction Kierunek lotu pocisku
 * @param sourcePlayerId ID gracza, który wystrzelił pocisk
 */
export function fireProjectileBolt(
  scene: THREE.Scene,
  startPosition: THREE.Vector3,
  direction: THREE.Vector3,
  sourcePlayerId?: string,
) {
  // Tworzenie pocisku - kula energii
  // Reduced segments for performance (8,8 instead of 12,12)
  const projectileGeometry = new THREE.SphereGeometry(0.3, 8, 8); // Większy i bardziej szczegółowy
  const projectileMaterial = new THREE.MeshPhongMaterial({
    color: PROJECTILE_CONFIG.LIGHTNING_COLOR,
    emissive: PROJECTILE_CONFIG.LIGHTNING_COLOR,
    emissiveIntensity: 1.0,
    shininess: 100,
    transparent: true,
    opacity: 0.8,
  });
  const projectile = new THREE.Mesh(projectileGeometry, projectileMaterial);

  // Ustawienie pozycji początkowej
  projectile.position.copy(startPosition);

  // Dodanie pocisku do sceny
  scene.add(projectile);

  // Parametry pocisku
  const projectileSpeed = PROJECTILE_CONFIG.SPEED;
  const gravity = -0.02; // Grawitacja
  let time = 0;

  // Dodaj efekt świetlny do pocisku - jaśniejszy i większy zasięg
  const projectileLight = new THREE.PointLight(
    PROJECTILE_CONFIG.LIGHTNING_COLOR,
    PROJECTILE_CONFIG.LIGHTNING_INTENSITY,
    PROJECTILE_CONFIG.LIGHTNING_RANGE,
  );
  projectileLight.position.copy(projectile.position);
  scene.add(projectileLight);

  // Dodaj efekt poświaty (glow)
  // Reduced segments for performance (8,8 instead of 16,16)
  const glowGeometry = new THREE.SphereGeometry(0.5, 8, 8);
  const glowMaterial = new THREE.MeshBasicMaterial({
    color: PROJECTILE_CONFIG.LIGHTNING_COLOR,
    transparent: true,
    opacity: 0.3,
    side: THREE.BackSide,
  });
  const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
  glowMesh.position.copy(projectile.position);
  scene.add(glowMesh);

  // Odtwórz dźwięk grzmotu przy wystrzeleniu
  // if (typeof playSound === 'function') {
  //   playSound('thunder', 0.7);
  // }

  // Funkcja tworząca efekt elektrycznych iskier za pociskiem
  const createLightningTrail = () => {
    // Tworzymy małe iskry elektryczne zamiast dymu
    const sparkGeometry = new THREE.SphereGeometry(0.05, 8, 8);
    const sparkMaterial = new THREE.MeshBasicMaterial({
      color: PROJECTILE_CONFIG.LIGHTNING_COLOR,
      transparent: true,
      opacity: 0.7,
    });
    const spark = new THREE.Mesh(sparkGeometry, sparkMaterial);

    // Losowa pozycja wokół pocisku
    const offset = new THREE.Vector3(
      (Math.random() - 0.5) * 0.3,
      (Math.random() - 0.5) * 0.3,
      (Math.random() - 0.5) * 0.3,
    );
    spark.position.copy(projectile.position).add(offset);
    scene.add(spark);

    // Animacja iskry
    let sparkTime = 0;
    const animateSpark = () => {
      sparkTime += 0.016;

      // Pulsowanie iskry
      const pulseScale = 1 + Math.sin(sparkTime * 20) * 0.3;
      spark.scale.set(pulseScale, pulseScale, pulseScale);

      // Zanikanie
      spark.material.opacity = Math.max(0, 0.7 - sparkTime * 1.4);

      if (sparkTime > 0.5) {
        // Krótszy czas życia niż dym
        scene.remove(spark);
        return;
      }

      requestAnimationFrame(animateSpark);
    };

    animateSpark();
  };

  // Funkcja tworząca efekt błyskawicy między pociskiem a celem
  const createLightningBolt = (start: THREE.Vector3, end: THREE.Vector3) => {
    const points = [];
    points.push(start.clone());

    // Tworzymy segmenty błyskawicy z losowymi odchyleniami
    let currentPoint = start.clone();
    const segments = PROJECTILE_CONFIG.LIGHTNING_SEGMENTS;
    const direction = end.clone().sub(start).normalize();
    const distance = start.distanceTo(end);
    const segmentLength = distance / segments;

    for (let i = 1; i < segments; i++) {
      // Obliczamy następny punkt na linii prostej
      const nextPoint = start.clone().add(direction.clone().multiplyScalar(segmentLength * i));

      // Dodajemy losowe odchylenie prostopadłe do kierunku
      const perpendicular = new THREE.Vector3().crossVectors(direction, new THREE.Vector3(0, 1, 0)).normalize();
      if (perpendicular.lengthSq() === 0) {
        perpendicular.crossVectors(direction, new THREE.Vector3(1, 0, 0)).normalize();
      }

      // Losowe odchylenie w płaszczyźnie prostopadłej
      const deviation = perpendicular.clone().multiplyScalar((Math.random() - 0.5) * segmentLength * 0.5);
      nextPoint.add(deviation);

      points.push(nextPoint);
      currentPoint = nextPoint.clone();

      // Losowo tworzymy odgałęzienia
      if (Math.random() < PROJECTILE_CONFIG.LIGHTNING_BRANCH_PROBABILITY && i > 0) {
        const branchDirection = direction.clone();
        // Losowy kąt odgałęzienia
        const branchAngle = (Math.random() - 0.5) * PROJECTILE_CONFIG.LIGHTNING_BRANCH_ANGLE;
        branchDirection.applyAxisAngle(perpendicular, branchAngle);

        // Tworzymy krótsze odgałęzienie
        const branchEnd = currentPoint
          .clone()
          .add(branchDirection.multiplyScalar(segmentLength * (Math.random() * 0.5 + 0.5)));
        const branchPoints = [];
        branchPoints.push(currentPoint.clone());

        // Dodajemy losowe punkty pośrednie dla odgałęzienia
        const midPoint = currentPoint.clone().add(branchDirection.clone().multiplyScalar(segmentLength * 0.5));
        midPoint.add(perpendicular.clone().multiplyScalar((Math.random() - 0.5) * segmentLength * 0.3));
        branchPoints.push(midPoint);
        branchPoints.push(branchEnd);

        // Tworzymy geometrię dla odgałęzienia
        const branchGeometry = new THREE.BufferGeometry().setFromPoints(branchPoints);
        const branchMaterial = new THREE.LineBasicMaterial({
          color: PROJECTILE_CONFIG.LIGHTNING_COLOR,
          linewidth: PROJECTILE_CONFIG.LIGHTNING_WIDTH,
          transparent: true,
          opacity: 0.7,
        });
        const branchLine = new THREE.Line(branchGeometry, branchMaterial);
        scene.add(branchLine);

        // Animacja zanikania odgałęzienia
        setTimeout(() => {
          scene.remove(branchLine);
          branchGeometry.dispose();
          branchMaterial.dispose();
        }, 100);
      }
    }

    points.push(end.clone());

    // Tworzymy geometrię dla głównej błyskawicy
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({
      color: PROJECTILE_CONFIG.LIGHTNING_COLOR,
      linewidth: PROJECTILE_CONFIG.LIGHTNING_WIDTH,
      transparent: true,
      opacity: 0.9,
    });
    const line = new THREE.Line(geometry, material);
    scene.add(line);

    // Animacja zanikania głównej błyskawicy
    setTimeout(() => {
      scene.remove(line);
      geometry.dispose();
      material.dispose();
    }, 150);
  };

  // Zapisz początkowy kierunek jako wektor prędkości
  const velocity = direction.clone().multiplyScalar(projectileSpeed);

  // Animacja lotu pocisku
  const animateProjectile = () => {
    time += 0.016; // ~60fps

    // Zastosuj grawitację do składowej Y prędkości, ale mniejszą dla błyskawicy
    velocity.y += gravity * 0.5; // Mniejsza grawitacja dla efektu "magicznego" pocisku

    // Aktualizacja pozycji na podstawie prędkości
    projectile.position.add(velocity);

    // Aktualizacja pozycji efektu poświaty
    glowMesh.position.copy(projectile.position);

    /*
    // Log pozycji pocisku co 10 klatek
    if (Math.floor(time * 60) % 10 === 0) {
      console.log("Projectile position at time " + time.toFixed(2) + ":", {
        x: projectile.position.x.toFixed(2),
        y: projectile.position.y.toFixed(2),
        z: projectile.position.z.toFixed(2),
        velocity: {
          x: velocity.x.toFixed(2),
          y: velocity.y.toFixed(2),
          z: velocity.z.toFixed(2),
        },
      });
    }
      */

    // Aktualizacja pozycji światła
    projectileLight.position.copy(projectile.position);

    // Dodaj efekt iskier elektrycznych co kilka klatek
    if (Math.random() > 0.5) {
      // Częstsze iskry
      createLightningTrail();
    }

    // Losowo twórz efekt błyskawicy w kierunku lotu
    if (Math.random() > 0.8) {
      const boltEnd = projectile.position.clone().add(velocity.clone().normalize().multiplyScalar(3));
      createLightningBolt(projectile.position.clone(), boltEnd);
    }

    // Pulsowanie światła dla efektu elektryczności
    projectileLight.intensity = PROJECTILE_CONFIG.LIGHTNING_INTENSITY * (0.8 + Math.sin(time * 20) * 0.2);

    // Pulsowanie rozmiaru pocisku dla efektu niestabilnej energii
    const pulseScale = 1 + Math.sin(time * 15) * 0.1;
    projectile.scale.set(pulseScale, pulseScale, pulseScale);

    // Efekt rotacji pocisku
    projectile.rotation.x += 0.05;
    projectile.rotation.y += 0.07;

    // Check for tank collisions
    const tanks = window.gameState?.tanks ?? [];

    const collisionRadius = 3;
    const collisionRadiusSq = collisionRadius * collisionRadius;
    const hitTank = tanks.find((tankInstance) => projectile.position.distanceToSquared(tankInstance.mesh.position) < collisionRadiusSq);

    // Handle tank hit
    if (hitTank) {
      // Get the Tank instance from the mesh's userData
      const tankInstance = hitTank as Tank;

      if (tankInstance) {
        // Przekazujemy sourcePlayerId do handleProjectileHit aby poprawnie przypisać kille
        handleProjectileHit(projectile.position, tankInstance, sourcePlayerId);

        // Usuwamy event trafienia pociskiem w billboard, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
        // document.dispatchEvent(new CustomEvent('projectile:hit', {
        //   detail: {
        //     position: projectile.position.clone(),
        //     direction: direction.clone(),
        //     playerId: sourcePlayerId || 'unknown'
        //   }
        // }));

        // Odtwórz dźwięk uderzenia pioruna
        // if (typeof playSound === 'function') {
        //   playSound('thunderstrike', 1.0);
        // }

        // Efekt błyskawic uderzających w cel
        for (let i = 0; i < 5; i++) {
          // Tworzymy błyskawice z różnych kierunków uderzające w cel
          const startOffset = new THREE.Vector3(
            (Math.random() - 0.5) * 10,
            5 + Math.random() * 5,
            (Math.random() - 0.5) * 10,
          );
          const startPos = projectile.position.clone().add(startOffset);
          createLightningBolt(startPos, projectile.position.clone());

          // Opóźnione tworzenie kolejnych błyskawic
          setTimeout(() => {
            if (scene) {
              // Sprawdź czy scena nadal istnieje
              const startPos2 = projectile.position
                .clone()
                .add(new THREE.Vector3((Math.random() - 0.5) * 8, 4 + Math.random() * 4, (Math.random() - 0.5) * 8));
              createLightningBolt(startPos2, projectile.position.clone());
            }
          }, i * 50);
        }

        // Create enhanced explosion effect at hit position with lightning color and increased intensity
        const explosion = createLightningExplosion(
          scene,
          projectile.position,
          1.5,
          PROJECTILE_CONFIG.LIGHTNING_COLOR,
          1.5,
        );
      } else {
        console.error("Tank hit but no Tank instance found in userData:", hitTank);
      }

      // Remove projectile and its light
      scene.remove(projectile);
      scene.remove(projectileLight);
      scene.remove(glowMesh);
      return;
    }

    // Sprawdzenie czy pocisk uderzył w ziemię
    if (projectile.position.y <= 0) {
      // Ustaw pozycję wybuchu na poziomie ziemi
      const explosionPosition = projectile.position.clone();
      explosionPosition.y = 0;

      /*
      console.log("Projectile hit ground at:", {
        x: explosionPosition.x.toFixed(2),
        y: explosionPosition.y.toFixed(2),
        z: explosionPosition.z.toFixed(2),
        flightTime: time.toFixed(2),
      });
      */

      // Usuwamy event trafienia pociskiem w billboard, ponieważ interakcja z billboardami odbywa się tylko poprzez kliknięcia myszką
      // document.dispatchEvent(new CustomEvent('projectile:hit', {
      //   detail: {
      //     position: projectile.position.clone(),
      //     direction: direction.clone(),
      //     playerId: sourcePlayerId || 'unknown'
      //   }
      // }));

      // Odtwórz dźwięk uderzenia pioruna
      // if (typeof playSound === 'function') {
      //   playSound('thunderstrike', 1.0);
      // }

      // Efekt błyskawic rozchodzących się po ziemi
      const groundRadius = 8; // Promień efektu na ziemi
      const boltCount = 8; // Liczba błyskawic

      for (let i = 0; i < boltCount; i++) {
        const angle = (i / boltCount) * Math.PI * 2;
        const endPos = explosionPosition
          .clone()
          .add(new THREE.Vector3(Math.cos(angle) * groundRadius, 0, Math.sin(angle) * groundRadius));

        // Tworzymy błyskawicę od punktu uderzenia
        createLightningBolt(explosionPosition.clone(), endPos);

        // Dodatkowe, opóźnione błyskawice dla lepszego efektu
        setTimeout(() => {
          if (scene) {
            // Sprawdź czy scena nadal istnieje
            const randomAngle = Math.random() * Math.PI * 2;
            const randomRadius = 3 + Math.random() * 5;
            const randomEndPos = explosionPosition
              .clone()
              .add(new THREE.Vector3(Math.cos(randomAngle) * randomRadius, 0, Math.sin(randomAngle) * randomRadius));
            createLightningBolt(explosionPosition.clone(), randomEndPos);
          }
        }, i * 30);
      }

      // Efekt wybuchu - większy i jaśniejszy z kolorem błyskawicy
      const explosion = createLightningExplosion(scene, explosionPosition, 1.5, PROJECTILE_CONFIG.LIGHTNING_COLOR, 1.5);

      scene.remove(projectile);
      scene.remove(projectileLight);
      scene.remove(glowMesh);
      return;
    }

    // Sprawdzenie maksymalnego czasu lotu
    if (time > PROJECTILE_CONFIG.RANGE / projectileSpeed) {
      // Efekt rozproszenia energii - błyskawice rozchodzące się we wszystkich kierunkach
      const boltCount = 12;
      for (let i = 0; i < boltCount; i++) {
        const angle1 = (i / boltCount) * Math.PI * 2;
        const angle2 = Math.random() * Math.PI;

        const endPos = projectile.position
          .clone()
          .add(
            new THREE.Vector3(
              Math.sin(angle2) * Math.cos(angle1) * 3,
              Math.cos(angle2) * 3,
              Math.sin(angle2) * Math.sin(angle1) * 3,
            ),
          );

        createLightningBolt(projectile.position.clone(), endPos);
      }

      // Efekt implozji - kula energii kurczy się przed zniknięciem
      const implodeAnimation = () => {
        projectile.scale.multiplyScalar(0.9);
        glowMesh.scale.multiplyScalar(0.9);
        projectileLight.intensity *= 0.9;

        if (projectile.scale.x > 0.1) {
          requestAnimationFrame(implodeAnimation);
        } else {
          scene.remove(projectile);
          scene.remove(projectileLight);
          scene.remove(glowMesh);
        }
      };

      implodeAnimation();
      return;
    }

    requestAnimationFrame(animateProjectile);
  };

  // Rozpocznij animację
  animateProjectile();

  // Zwróć referencję do pocisku (może być przydatne dla zewnętrznych funkcji)
  return {
    projectile,
    light: projectileLight,
    glow: glowMesh,
  };
}
